// In-depth Java lessons (Codeazy lesson standard).

export const javaLessons = [
  {
    slug: "oop-basics",
    title: "Object-Oriented Programming in Java",
    summary: "Classes and objects, the four pillars, interfaces vs abstract classes, and equals/hashCode, explained with real examples.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Java is object-oriented to its core, and Spring Boot, the most-used Java backend framework, is built entirely on OOP ideas such as interfaces, dependency injection and polymorphism. OOP questions appear in nearly every Java interview, from freshers to senior engineers." },

      { type: "h2", text: "The mental model: blueprints and houses" },
      { type: "p", text: "A **class** is an architect’s blueprint. An **object** is a house built from it. One blueprint can produce many houses, each with its own paint colour and furniture (field values), but all with the same rooms and doors (structure and methods)." },
      { type: "code", language: "java", code: `public class Student {
    // fields (state)
    private final String name;
    private int score;

    // constructor
    public Student(String name, int score) {
        this.name = name;
        this.score = score;
    }

    // methods (behaviour)
    public boolean hasPassed() {
        return score >= 40;
    }

    public String getName() { return name; }
    public int getScore() { return score; }
}

Student asha = new Student("Asha", 82);   // an object (instance)
Student ravi = new Student("Ravi", 35);
System.out.println(asha.hasPassed() + " " + ravi.hasPassed());`, output: `true false` },

      { type: "h2", text: "Pillar 1: Encapsulation" },
      { type: "p", text: "Hide the internal state and expose only safe operations. Fields are `private`, and changes go through methods that enforce the rules:" },
      { type: "code", language: "java", code: `public class BankAccount {
    private double balance;     // nobody outside can set this directly

    public void deposit(double amount) {
        if (amount <= 0) throw new IllegalArgumentException("Amount must be positive");
        balance += amount;
    }

    public void withdraw(double amount) {
        if (amount > balance) throw new IllegalStateException("Insufficient funds");
        balance -= amount;
    }

    public double getBalance() { return balance; }
}` },
      { type: "table", headers: ["Access modifier", "Same class", "Same package", "Subclass", "Everywhere"], rows: [
        ["`private`", "✓", "", "", ""],
        ["(default, no keyword)", "✓", "✓", "", ""],
        ["`protected`", "✓", "✓", "✓", ""],
        ["`public`", "✓", "✓", "✓", "✓"]
      ] },

      { type: "h2", text: "Pillar 2: Inheritance" },
      { type: "code", language: "java", code: `public class Employee {
    protected final String name;
    protected final double baseSalary;

    public Employee(String name, double baseSalary) {
        this.name = name;
        this.baseSalary = baseSalary;
    }

    public double monthlyPay() { return baseSalary / 12; }
}

public class Manager extends Employee {
    private final double bonus;

    public Manager(String name, double baseSalary, double bonus) {
        super(name, baseSalary);          // call the parent constructor first
        this.bonus = bonus;
    }

    @Override
    public double monthlyPay() {
        return super.monthlyPay() + bonus / 12;
    }
}` },
      { type: "callout", variant: "note", text: "Java supports **single inheritance** for classes (one `extends`) but a class can implement **many interfaces**. Every class ultimately extends `java.lang.Object`." },

      { type: "h2", text: "Pillar 3: Polymorphism" },
      { type: "p", text: "One reference type, many behaviours. The method that runs is chosen at **runtime** based on the actual object, which is called dynamic dispatch:" },
      { type: "code", language: "java", code: `List<Employee> staff = List.of(
    new Employee("Asha", 1_200_000),
    new Manager("Ravi", 2_400_000, 600_000)
);

for (Employee e : staff) {
    System.out.println(e.name + ": " + e.monthlyPay());   // the right version runs
}`, output: `Asha: 100000.0
Ravi: 250000.0` },
      { type: "table", headers: ["", "Overloading (compile-time)", "Overriding (runtime)"], rows: [
        ["What", "Same method name, different parameters, in the same class", "A subclass redefines a parent method with the same signature"],
        ["Decided", "At compile time, by the argument types", "At runtime, by the object’s actual type"],
        ["Example", "`print(int)` and `print(String)`", "`Manager.monthlyPay()` overrides `Employee.monthlyPay()`"]
      ] },

      { type: "h2", text: "Pillar 4: Abstraction" },
      { type: "p", text: "Expose **what** something does and hide **how**. In Java this means interfaces and abstract classes:" },
      { type: "code", language: "java", code: `public interface PaymentGateway {
    PaymentResult charge(String customerId, long amountInPaise);
}

public class RazorpayGateway implements PaymentGateway {
    @Override
    public PaymentResult charge(String customerId, long amountInPaise) {
        // Razorpay-specific HTTP calls...
        return PaymentResult.success("rzp_123");
    }
}

public class CheckoutService {
    private final PaymentGateway gateway;          // depends on the abstraction

    public CheckoutService(PaymentGateway gateway) {
        this.gateway = gateway;                    // any implementation can be injected
    }

    public void checkout(String customerId, long amount) {
        gateway.charge(customerId, amount);
    }
}` },
      { type: "callout", variant: "tip", text: "Because `CheckoutService` depends on an interface, you can swap Razorpay for Stripe or a fake gateway in tests without changing it. This is exactly how Spring’s dependency injection works." },

      { type: "h2", text: "Interface vs abstract class" },
      { type: "table", headers: ["", "Interface", "Abstract class"], rows: [
        ["Purpose", "A contract (“can do”)", "A partial base implementation (“is a”)"],
        ["Multiple inheritance", "A class can implement many", "A class can extend only one"],
        ["Fields", "Only `public static final` constants", "Any fields, including instance state"],
        ["Methods", "Abstract, `default`, `static` and `private` methods (Java 8+/9+)", "Abstract and concrete methods"],
        ["Constructors", "No", "Yes"],
        ["Use when", "Unrelated classes share a capability (`Comparable`, `Runnable`)", "Closely related classes share code and state"]
      ] },

      { type: "h2", text: "equals() and hashCode()" },
      { type: "code", language: "java", code: `Student a = new Student("Asha", 82);
Student b = new Student("Asha", 82);

System.out.println(a == b);        // same object in memory?
System.out.println(a.equals(b));   // Object.equals defaults to ==`, output: `false
false` },
      { type: "p", text: "Override both methods so objects with the same data are equal. Hash-based collections such as `HashMap` and `HashSet` use `hashCode` to find the bucket and then `equals` to compare:" },
      { type: "code", language: "java", code: `@Override
public boolean equals(Object o) {
    if (this == o) return true;
    if (!(o instanceof Student other)) return false;      // pattern matching (Java 16+)
    return score == other.score && name.equals(other.name);
}

@Override
public int hashCode() {
    return Objects.hash(name, score);
}` },
      { type: "callout", variant: "warning", text: "The contract: if `a.equals(b)`, then `a.hashCode() == b.hashCode()` **must** be true. Override `equals` without `hashCode` and `HashSet` will happily store “duplicates” and `HashMap.get` will fail to find your keys." },
      { type: "h3", text: "Records: data classes in one line (Java 16+)" },
      { type: "code", language: "java", code: `public record Course(String id, String title, int priceInRupees) {}

Course c = new Course("dsa", "DSA", 4999);
System.out.println(c.title());                       // accessor
System.out.println(c.equals(new Course("dsa", "DSA", 4999)));
System.out.println(c);`, output: `DSA
true
Course[id=dsa, title=DSA, priceInRupees=4999]` },
      { type: "p", text: "Records are immutable and generate the constructor, accessors, `equals`, `hashCode` and `toString` automatically, which makes them perfect for DTOs." },

      { type: "h2", text: "Composition over inheritance" },
      { type: "compare", badLabel: "Inheritance for code reuse", bad: `class Logger { void log(String m) {...} }

// A course "is a" logger? No.
class CourseService extends Logger {
    void enroll() { log("enrolled"); }
}`, goodLabel: "Has-a relationship", good: `class CourseService {
    private final Logger logger;   // has a logger
    CourseService(Logger logger) { this.logger = logger; }
    void enroll() { logger.log("enrolled"); }
}` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Explain the four pillars of OOP.", answer: ["**Encapsulation** keeps state private and exposes behaviour through methods. **Inheritance** lets a class reuse and extend another with `extends`. **Polymorphism** means one interface with many implementations: overloading at compile time and overriding at runtime. **Abstraction** exposes what an object does while hiding how, through interfaces and abstract classes."] },
      { type: "qa", question: "What is the difference between an abstract class and an interface?", answer: ["A class can implement many interfaces but extend only one abstract class. Interfaces define contracts and can have default, static and private methods but no instance state. Abstract classes can have constructors, fields and concrete methods. Use interfaces for capabilities and abstract classes for shared base implementations of closely related types."] },
      { type: "qa", question: "Why must you override `hashCode` when overriding `equals`?", answer: ["Hash-based collections use `hashCode` to choose a bucket and `equals` to compare within it. If two equal objects have different hash codes, they land in different buckets, so `HashSet` stores duplicates and `HashMap` lookups fail. The contract requires equal objects to have equal hash codes."] },
      { type: "qa", question: "What is the difference between `==` and `.equals()`?", answer: ["`==` compares references for objects (whether they are the same instance) and values for primitives. `.equals()` compares logical equality as defined by the class. `String.equals` compares characters, for example. Always compare strings with `.equals()`."] },
      { type: "qa", question: "Can you override a static method?", answer: ["No. Static methods belong to the class, not to instances, so they are resolved at compile time. A subclass can declare a static method with the same signature, but that is **method hiding**, not overriding, and there is no runtime polymorphism."] },
      { type: "qa", question: "What are records and when would you use them?", answer: ["Records (Java 16+) are concise immutable data carriers. The compiler generates the canonical constructor, accessors, `equals`, `hashCode` and `toString`. Use them for DTOs, API request and response objects, value objects and map keys."] },

      { type: "exercise", title: "Model notifications with polymorphism", task: "Create a `Notification` interface with `String send(String userId, String message)`. Implement `EmailNotification` and `SmsNotification`, where SMS truncates messages to 160 characters. Write a `NotificationService` that receives a `List<Notification>` through its constructor and sends a message through all of them, returning the results.", language: "java", solution: `public interface Notification {
    String send(String userId, String message);
}

public class EmailNotification implements Notification {
    @Override
    public String send(String userId, String message) {
        return "Email to " + userId + ": " + message;
    }
}

public class SmsNotification implements Notification {
    private static final int LIMIT = 160;

    @Override
    public String send(String userId, String message) {
        String body = message.length() > LIMIT ? message.substring(0, LIMIT) : message;
        return "SMS to " + userId + ": " + body;
    }
}

public class NotificationService {
    private final List<Notification> channels;

    public NotificationService(List<Notification> channels) {
        this.channels = List.copyOf(channels);   // defensive, immutable copy
    }

    public List<String> notifyAll(String userId, String message) {
        return channels.stream()
                .map(channel -> channel.send(userId, message))
                .toList();
    }
}

// Usage
var service = new NotificationService(List.of(new EmailNotification(), new SmsNotification()));
service.notifyAll("u42", "Your DSA cohort starts on 16 Nov!")
       .forEach(System.out::println);`, output: `Email to u42: Your DSA cohort starts on 16 Nov!
SMS to u42: Your DSA cohort starts on 16 Nov!`, explanation: "`NotificationService` depends only on the interface, so adding a `WhatsAppNotification` needs no changes to it. This is the open/closed principle. Constructor injection of the list is exactly how Spring wires every bean that implements an interface." },

      { type: "summary", items: [
        "Classes are blueprints, and objects are instances with their own state.",
        "Encapsulate state, inherit only for real “is-a” relationships, and prefer composition otherwise.",
        "Polymorphism lets code depend on interfaces while the runtime type picks the behaviour.",
        "Use interfaces for capabilities and abstract classes for shared base code.",
        "Override `equals` and `hashCode` together, or use records for data classes."
      ] }
    ]
  },

  {
    slug: "collections",
    title: "The Collections Framework",
    summary: "List, Set, Map and Queue: choosing the right one, how HashMap works inside, sorting, and immutability.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Almost every Java program stores groups of objects, and choosing the wrong collection can turn an O(1) operation into O(n). “How does HashMap work internally?” is one of the most frequently asked Java interview questions." },

      { type: "h2", text: "The mental model: the right container for the job" },
      { type: "p", text: "A **List** is a numbered shelf (order matters, duplicates are allowed). A **Set** is a bag of unique marbles (no duplicates). A **Map** is a dictionary (look up a value by its key). A **Queue** is a ticket line (process items in order)." },
      { type: "code", language: "text", code: `Iterable
 └── Collection
      ├── List   → ArrayList, LinkedList
      ├── Set    → HashSet, LinkedHashSet, TreeSet
      └── Queue  → ArrayDeque, PriorityQueue, LinkedList

Map (separate hierarchy) → HashMap, LinkedHashMap, TreeMap, ConcurrentHashMap` },

      { type: "h2", text: "Choosing a collection" },
      { type: "table", headers: ["Need", "Use", "Key operations"], rows: [
        ["Ordered list, fast access by index", "`ArrayList`", "get O(1), add at end amortised O(1), insert in the middle O(n)"],
        ["Unique items, fast lookup", "`HashSet`", "add, contains and remove O(1) on average"],
        ["Unique items in insertion order", "`LinkedHashSet`", "O(1) and predictable iteration"],
        ["Unique items, sorted", "`TreeSet`", "O(log n), plus `first()`, `floor()` and range views"],
        ["Key → value lookup", "`HashMap`", "get and put O(1) on average"],
        ["Key → value in insertion or access order", "`LinkedHashMap`", "Easy LRU caches"],
        ["Key → value sorted by key", "`TreeMap`", "O(log n), plus `firstKey()`, `headMap()` and range queries"],
        ["Stack or queue", "`ArrayDeque`", "push, pop, offer and poll O(1)"],
        ["Always get the smallest or largest", "`PriorityQueue`", "offer and poll O(log n), peek O(1)"]
      ] },
      { type: "callout", variant: "tip", text: "Program to interfaces: `List<String> names = new ArrayList<>();`. Your code then depends on what a list does, not on how it is implemented." },

      { type: "h2", text: "List" },
      { type: "code", language: "java", code: `List<String> courses = new ArrayList<>(List.of("DSA", "Java", "React"));
courses.add("System Design");
courses.add(1, "Spring Boot");          // insert at index 1
courses.remove("React");
System.out.println(courses);
System.out.println(courses.get(0) + ", size=" + courses.size());`, output: `[DSA, Spring Boot, Java, System Design]
DSA, size=4` },
      { type: "table", headers: ["", "ArrayList", "LinkedList"], rows: [
        ["Backed by", "A resizable array", "A doubly linked list"],
        ["`get(i)`", "O(1)", "O(n)"],
        ["Add or remove at the ends", "Amortised O(1) at the end, O(n) at the front", "O(1)"],
        ["Memory", "Compact", "Extra memory per node for pointers"],
        ["In practice", "The default choice", "Rarely better. Use `ArrayDeque` for queues"]
      ] },

      { type: "h2", text: "Set" },
      { type: "code", language: "java", code: `Set<String> skills = new HashSet<>();
skills.add("Java");
skills.add("SQL");
boolean added = skills.add("Java");      // duplicate
System.out.println(added + " " + skills.size());

Set<Integer> sorted = new TreeSet<>(List.of(42, 7, 19, 7, 3));
System.out.println(sorted);`, output: `false 2
[3, 7, 19, 42]` },

      { type: "h2", text: "Map" },
      { type: "code", language: "java", code: `Map<String, Integer> wordCount = new HashMap<>();
String[] words = "to be or not to be".split(" ");

for (String w : words) {
    wordCount.merge(w, 1, Integer::sum);       // add 1, or start at 1
}
System.out.println(wordCount.get("to") + " " + wordCount.getOrDefault("java", 0));

// Group students by city
Map<String, List<String>> byCity = new TreeMap<>();
byCity.computeIfAbsent("Pune", k -> new ArrayList<>()).add("Asha");
byCity.computeIfAbsent("Delhi", k -> new ArrayList<>()).add("Ravi");
byCity.computeIfAbsent("Pune", k -> new ArrayList<>()).add("Meera");
System.out.println(byCity);

for (Map.Entry<String, List<String>> e : byCity.entrySet()) {
    System.out.println(e.getKey() + " → " + e.getValue().size());
}`, output: `2 0
{Delhi=[Ravi], Pune=[Asha, Meera]}
Delhi → 1
Pune → 2` },

      { type: "h2", text: "How HashMap works inside" },
      { type: "ol", items: [
        "HashMap holds an **array of buckets**, 16 by default.",
        "`put(key, value)` calls `key.hashCode()`, mixes the bits, and picks bucket `index = hash & (capacity - 1)`.",
        "If the bucket is empty, the entry goes there. If not, that is a **collision**: entries in the same bucket form a linked list, and `equals()` decides whether the key already exists (replace) or is new (append).",
        "Since Java 8, a bucket with more than 8 entries (in a table of at least 64 buckets) turns into a **red-black tree**, so the worst-case lookup becomes O(log n) instead of O(n).",
        "When the number of entries exceeds capacity × **load factor** (0.75 by default), the table **doubles** and all entries are rehashed."
      ] },
      { type: "code", language: "text", code: `buckets: [0]  →  null
         [1]  →  ("Java", 3)
         [2]  →  ("DSA", 5) → ("SQL", 2)     ← collision: same bucket, different keys
         [3]  →  null
         ...
         [15] →  ("React", 4)` },
      { type: "callout", variant: "warning", text: "Never mutate an object’s fields that are used in `hashCode` while it is a key in a HashMap. Its hash changes, the map looks in the wrong bucket, and the entry becomes unreachable." },

      { type: "h2", text: "Queues, stacks and priority queues" },
      { type: "code", language: "java", code: `Deque<String> stack = new ArrayDeque<>();
stack.push("a"); stack.push("b"); stack.push("c");
System.out.println(stack.pop());        // LIFO

Queue<String> queue = new ArrayDeque<>();
queue.offer("first"); queue.offer("second");
System.out.println(queue.poll());       // FIFO

// Min-heap by default; reverse for a max-heap
PriorityQueue<Integer> maxHeap = new PriorityQueue<>(Comparator.reverseOrder());
maxHeap.addAll(List.of(5, 1, 9, 3));
System.out.println(maxHeap.poll() + " " + maxHeap.poll());`, output: `c
first
9 5` },

      { type: "h2", text: "Sorting with Comparable and Comparator" },
      { type: "code", language: "java", code: `record Student(String name, int score, String city) {}

List<Student> students = new ArrayList<>(List.of(
    new Student("Ravi", 88, "Delhi"),
    new Student("Asha", 92, "Pune"),
    new Student("Meera", 88, "Pune")
));

// score descending, then name ascending
students.sort(
    Comparator.comparingInt(Student::score).reversed()
              .thenComparing(Student::name)
);
students.forEach(s -> System.out.println(s.name() + " " + s.score()));`, output: `Asha 92
Meera 88
Ravi 88` },
      { type: "table", headers: ["", "Comparable", "Comparator"], rows: [
        ["Defined", "Inside the class: `compareTo(T other)`", "Outside, as a separate object or lambda"],
        ["Number of orderings", "One natural ordering", "As many as you need"],
        ["Used by", "`Collections.sort(list)`, `TreeSet`", "`list.sort(cmp)`, `new TreeSet<>(cmp)`"]
      ] },

      { type: "h2", text: "Immutability and fail-fast iterators" },
      { type: "code", language: "java", code: `List<String> fixed = List.of("a", "b");   // immutable
fixed.add("c");                            // throws UnsupportedOperationException

List<String> names = new ArrayList<>(List.of("Asha", "Ravi", "Meera"));
for (String n : names) {
    if (n.startsWith("R")) names.remove(n);   // ConcurrentModificationException
}

names.removeIf(n -> n.startsWith("R"));     // the safe way` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How does HashMap work internally?", answer: ["It stores entries in an array of buckets. A key’s `hashCode` is spread and masked to choose a bucket. Collisions are chained in a linked list, which becomes a red-black tree when a bucket grows beyond 8 entries. `equals` identifies the key within a bucket. When size exceeds capacity × 0.75, the array doubles and entries are rehashed. Average get and put are O(1)."] },
      { type: "qa", question: "What is the difference between HashMap, LinkedHashMap and TreeMap?", answer: ["HashMap has no ordering guarantee and O(1) average operations. LinkedHashMap keeps insertion order, or access order, which is useful for LRU caches, and is still O(1). TreeMap keeps keys sorted using a red-black tree, with O(log n) operations and navigation methods such as `floorKey` and `subMap`."] },
      { type: "qa", question: "What is the difference between HashMap and ConcurrentHashMap?", answer: ["HashMap isn’t thread-safe, and concurrent modification can corrupt it. ConcurrentHashMap supports high-concurrency reads and updates using fine-grained locking and CAS operations, offers atomic methods such as `computeIfAbsent` and `merge`, and doesn’t allow null keys or values."] },
      { type: "qa", question: "ArrayList vs LinkedList: which would you use?", answer: ["Almost always ArrayList: O(1) random access, cache-friendly contiguous memory, and fast appends. LinkedList only wins for frequent insertions and removals at the ends or through an iterator, and `ArrayDeque` is usually better for that too."] },
      { type: "qa", question: "What causes ConcurrentModificationException?", answer: ["Structurally modifying a collection while iterating over it with a for-each loop, other than through the iterator’s own `remove`. Fail-fast iterators detect the change through a modification counter. Use `iterator.remove()`, `removeIf`, or collect the changes and apply them afterwards."] },
      { type: "qa", question: "What is the difference between Comparable and Comparator?", answer: ["`Comparable` defines a class’s single natural ordering through `compareTo`. `Comparator` is an external strategy, so you can define many orderings and compose them with `comparing`, `thenComparing` and `reversed`."] },

      { type: "exercise", title: "Build an LRU cache", task: "Implement `LRUCache<K, V>` with a fixed capacity: `get(key)` returns the value (or null) and marks it most recently used, and `put(key, value)` inserts or updates and evicts the least recently used entry when over capacity. Both must be O(1). Hint: `LinkedHashMap` has an access-order mode.", language: "java", solution: `import java.util.LinkedHashMap;
import java.util.Map;

public class LRUCache<K, V> extends LinkedHashMap<K, V> {
    private final int capacity;

    public LRUCache(int capacity) {
        super(16, 0.75f, true);      // true = access order (get moves to the end)
        this.capacity = capacity;
    }

    @Override
    protected boolean removeEldestEntry(Map.Entry<K, V> eldest) {
        return size() > capacity;    // evict the least recently used entry
    }
}

LRUCache<String, String> cache = new LRUCache<>(2);
cache.put("a", "A");
cache.put("b", "B");
cache.get("a");                      // "a" is now most recent
cache.put("c", "C");                 // evicts "b"
System.out.println(cache.keySet());`, output: `[a, c]`, explanation: "In access-order mode, every `get` or `put` moves the entry to the end of the internal linked list, so the head is always the least recently used. `removeEldestEntry` is called after each insertion. Both operations stay O(1). In interviews, also be ready to build this from scratch with a HashMap plus a doubly linked list." },

      { type: "summary", items: [
        "List is ordered with duplicates, Set is unique, Map is key → value, and Queue/Deque are for FIFO, LIFO or priority.",
        "Defaults: `ArrayList`, `HashSet`, `HashMap` and `ArrayDeque`. Use the Tree variants when you need sorted order.",
        "HashMap uses buckets, `hashCode` and `equals`, treeifies long buckets, and resizes at a 0.75 load factor.",
        "Sort with `Comparator.comparing(...).thenComparing(...)`.",
        "Use `List.of` for immutability, and `removeIf` instead of removing inside a for-each loop."
      ] }
    ]
  },

  {
    slug: "exceptions",
    title: "Exception Handling",
    summary: "Checked vs unchecked exceptions, try-with-resources, custom exceptions and how real applications handle errors.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Real systems fail: networks drop, files are missing and users send bad input. Good exception handling keeps your app running, gives users helpful messages and gives developers the information they need to fix bugs. Swallowed exceptions are among the hardest bugs to track down." },

      { type: "h2", text: "The mental model: a fire alarm" },
      { type: "p", text: "Throwing an exception is pulling a **fire alarm**: normal work stops immediately and the alarm travels up the call stack, floor by floor, until someone (a `catch` block) responds to it. If nobody does, the building (the thread) is evacuated and the program crashes with a stack trace. A `finally` block is the cleanup crew that always runs, alarm or not." },

      { type: "h2", text: "The exception hierarchy" },
      { type: "code", language: "text", code: `Throwable
 ├── Error                      (JVM problems: don't catch)
 │    ├── OutOfMemoryError
 │    └── StackOverflowError
 └── Exception                  (checked: must be caught or declared)
      ├── IOException
      ├── SQLException
      └── RuntimeException       (unchecked: optional to handle)
           ├── NullPointerException
           ├── IllegalArgumentException
           ├── IllegalStateException
           ├── ArithmeticException
           └── IndexOutOfBoundsException` },
      { type: "table", headers: ["", "Checked", "Unchecked (RuntimeException)"], rows: [
        ["Compiler forces handling", "Yes: catch it or declare `throws`", "No"],
        ["Represents", "Recoverable conditions outside your control (I/O, network)", "Programming errors or invalid states (null, bad arguments)"],
        ["Examples", "`IOException`, `SQLException`", "`NullPointerException`, `IllegalArgumentException`"]
      ] },

      { type: "h2", text: "try, catch, finally" },
      { type: "code", language: "java", code: `public static int parseAge(String input) {
    try {
        int age = Integer.parseInt(input);
        if (age < 0) throw new IllegalArgumentException("Age cannot be negative");
        return age;
    } catch (NumberFormatException e) {
        System.out.println("Not a number: " + input);
        return -1;
    } finally {
        System.out.println("parseAge done");
    }
}

System.out.println(parseAge("21"));
System.out.println(parseAge("abc"));`, output: `parseAge done
21
Not a number: abc
parseAge done
-1` },
      { type: "p", text: "Notice that `finally` runs **before** the method actually returns, even when the `try` block has a `return`." },
      { type: "h3", text: "Multi-catch and ordering" },
      { type: "code", language: "java", code: `try {
    processFile(path);
} catch (FileNotFoundException e) {        // more specific first
    log.warn("Missing file {}", path);
} catch (IOException | ParseException e) { // multi-catch for the same handling
    log.error("Could not process {}", path, e);
}` },

      { type: "h2", text: "try-with-resources" },
      { type: "p", text: "Anything that implements `AutoCloseable` (files, database connections, streams) is closed automatically, even if an exception is thrown:" },
      { type: "compare", badLabel: "Manual cleanup (verbose and leaky)", bad: `BufferedReader reader = null;
try {
    reader = new BufferedReader(new FileReader("data.csv"));
    return reader.readLine();
} finally {
    if (reader != null) {
        try { reader.close(); } catch (IOException ignored) {}
    }
}`, goodLabel: "try-with-resources", good: `try (BufferedReader reader =
         Files.newBufferedReader(Path.of("data.csv"))) {
    return reader.readLine();
}
// reader.close() is called automatically` },

      { type: "h2", text: "Custom exceptions" },
      { type: "code", language: "java", code: `public class CourseNotFoundException extends RuntimeException {
    private final String courseId;

    public CourseNotFoundException(String courseId) {
        super("Course not found: " + courseId);
        this.courseId = courseId;
    }

    public String getCourseId() { return courseId; }
}

public class SeatsUnavailableException extends RuntimeException {
    public SeatsUnavailableException(String cohortId) {
        super("No seats left in cohort " + cohortId);
    }
}

// usage in a service
public Course getCourse(String id) {
    return repository.findById(id)
        .orElseThrow(() -> new CourseNotFoundException(id));
}` },
      { type: "callout", variant: "tip", text: "Name exceptions after the problem (`CourseNotFoundException`), not the location (`ServiceException`). Modern Java and Spring codebases prefer unchecked custom exceptions, translated to HTTP responses in one central place." },

      { type: "h2", text: "Exception chaining" },
      { type: "code", language: "java", code: `try {
    return objectMapper.readValue(json, Order.class);
} catch (JsonProcessingException e) {
    // keep the original cause, so the full stack trace is preserved
    throw new InvalidOrderException("Malformed order payload", e);
}` },

      { type: "h2", text: "Best practices" },
      { type: "compare", badLabel: "Swallowing exceptions", bad: `try {
    sendInvoiceEmail(order);
} catch (Exception e) {
    // TODO
}`, goodLabel: "Handle or propagate with context", good: `try {
    sendInvoiceEmail(order);
} catch (MailException e) {
    log.error("Invoice email failed for order {}", order.id(), e);
    retryQueue.add(order.id());
}` },
      { type: "ul", items: [
        "Catch the **most specific** exception you can actually handle. Don’t catch `Exception` or `Throwable` by reflex.",
        "Never catch an exception just to ignore it. Log it with context and the exception object, or rethrow it.",
        "Don’t use exceptions for normal control flow, such as finding a value in a list.",
        "Validate arguments early (`Objects.requireNonNull`, `IllegalArgumentException`) and fail fast.",
        "Throw early, catch late: let exceptions bubble up to a layer that can make a decision, such as an HTTP error handler."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between checked and unchecked exceptions?", answer: ["Checked exceptions (subclasses of `Exception` but not of `RuntimeException`) must be caught or declared with `throws`, and they represent recoverable external conditions such as I/O. Unchecked exceptions (`RuntimeException` and its subclasses) aren’t enforced by the compiler, and usually signal programming errors or invalid states."] },
      { type: "qa", question: "Does `finally` always run?", answer: ["Almost always: after `try` completes normally, after a `catch`, and even when `try` returns. It doesn’t run if the JVM exits (`System.exit`), the thread is killed or the machine crashes. Avoid `return` in `finally`, because it overrides the try’s return value and silently discards exceptions."] },
      { type: "qa", question: "What is try-with-resources?", answer: ["A `try` statement that declares resources implementing `AutoCloseable`. They are closed automatically in reverse order when the block exits, normally or by exception. Exceptions thrown while closing are attached as suppressed exceptions instead of hiding the original one."] },
      { type: "qa", question: "What is the difference between `throw` and `throws`?", answer: ["`throw` actually throws an exception instance: `throw new IllegalStateException(...)`. `throws` in a method signature declares which checked exceptions the method may propagate to callers."] },
      { type: "qa", question: "Should you catch `Error`?", answer: ["Generally no. Errors such as `OutOfMemoryError` and `StackOverflowError` indicate serious JVM problems that an application usually can’t recover from. Let them propagate, log them at the top level, and let the process restart."] },

      { type: "exercise", title: "Safe CSV import", task: "Write `List<Student> importStudents(Path csv)` that reads lines of `name,score`, skips the header, uses try-with-resources, skips (and counts) lines with an invalid score instead of failing the whole import, and wraps any `IOException` in a custom unchecked `ImportFailedException` that keeps the cause. Print how many lines were skipped.", language: "java", solution: `public class ImportFailedException extends RuntimeException {
    public ImportFailedException(String message, Throwable cause) {
        super(message, cause);
    }
}

public static List<Student> importStudents(Path csv) {
    List<Student> students = new ArrayList<>();
    int skipped = 0;

    try (BufferedReader reader = Files.newBufferedReader(csv)) {
        reader.readLine();                               // skip header
        String line;
        while ((line = reader.readLine()) != null) {
            String[] parts = line.split(",");
            try {
                students.add(new Student(parts[0].trim(), Integer.parseInt(parts[1].trim())));
            } catch (NumberFormatException | ArrayIndexOutOfBoundsException e) {
                skipped++;                                // bad row: skip, don't crash
            }
        }
    } catch (IOException e) {
        throw new ImportFailedException("Could not read " + csv, e);   // chained cause
    }

    System.out.println("Imported " + students.size() + ", skipped " + skipped);
    return students;
}`, explanation: "Row-level problems are expected and recoverable, so they are counted and skipped. File-level I/O failure is fatal for this operation, so it is wrapped with context and the original cause is kept. The reader is always closed by try-with-resources." },

      { type: "summary", items: [
        "Exceptions travel up the stack until caught, and `finally` always runs for cleanup.",
        "Checked exceptions are for recoverable external problems. Unchecked ones are for bugs and invalid states.",
        "Use try-with-resources for anything closeable.",
        "Create meaningful custom exceptions, and chain causes when wrapping.",
        "Never swallow exceptions. Catch specific types, log them with context, and handle them centrally."
      ] }
    ]
  },

  {
    slug: "streams",
    title: "Streams and Lambdas",
    summary: "Functional-style data processing: lambdas, method references, stream pipelines, collectors and Optional.",
    readTime: 14,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Since Java 8, streams and lambdas have become the standard way to process collections in modern Java and Spring code. They make data transformations shorter and clearer, and stream questions, especially `groupingBy`, are interview favourites." },

      { type: "h2", text: "The mental model: a factory conveyor belt" },
      { type: "p", text: "A stream is a **conveyor belt** carrying items from a source (a list) through workstations: `filter` removes rejects, `map` transforms each item and `sorted` reorders them. Nothing moves until a **terminal operation** such as `toList` or `count` switches the belt on. The belt runs once and can’t be rewound." },

      { type: "h2", text: "Lambdas and functional interfaces" },
      { type: "p", text: "A lambda is a short anonymous function. It can be used wherever a **functional interface** (an interface with exactly one abstract method) is expected:" },
      { type: "code", language: "java", code: `// Before Java 8
Comparator<String> byLength = new Comparator<String>() {
    @Override
    public int compare(String a, String b) {
        return Integer.compare(a.length(), b.length());
    }
};

// Lambda
Comparator<String> byLength2 = (a, b) -> Integer.compare(a.length(), b.length());

// Method reference
Comparator<String> byLength3 = Comparator.comparingInt(String::length);` },
      { type: "table", headers: ["Interface", "Method", "Shape", "Example"], rows: [
        ["`Predicate<T>`", "`test`", "T → boolean", "`s -> s.getScore() >= 40`"],
        ["`Function<T, R>`", "`apply`", "T → R", "`s -> s.getName()`"],
        ["`Consumer<T>`", "`accept`", "T → void", "`s -> System.out.println(s)`"],
        ["`Supplier<T>`", "`get`", "() → T", "`() -> new ArrayList<>()`"],
        ["`BiFunction<T, U, R>`", "`apply`", "(T, U) → R", "`(a, b) -> a + b`"],
        ["`UnaryOperator<T>`", "`apply`", "T → T", "`s -> s.toUpperCase()`"]
      ] },
      { type: "table", headers: ["Method reference", "Equivalent lambda"], rows: [
        ["`String::toUpperCase`", "`s -> s.toUpperCase()`"],
        ["`System.out::println`", "`x -> System.out.println(x)`"],
        ["`Integer::parseInt`", "`s -> Integer.parseInt(s)`"],
        ["`Student::new`", "`name -> new Student(name)`"]
      ] },

      { type: "h2", text: "A stream pipeline" },
      { type: "code", language: "java", code: `record Student(String name, int score, String city) {}

List<Student> students = List.of(
    new Student("Asha", 92, "Pune"),
    new Student("Ravi", 35, "Delhi"),
    new Student("Meera", 88, "Pune"),
    new Student("Kabir", 74, "Delhi"),
    new Student("Zoya", 81, "Mumbai")
);

List<String> toppers = students.stream()          // source
    .filter(s -> s.score() >= 80)                 // intermediate
    .sorted(Comparator.comparingInt(Student::score).reversed())
    .map(Student::name)                           // intermediate
    .toList();                                    // terminal

System.out.println(toppers);`, output: `[Asha, Meera, Zoya]` },
      { type: "table", headers: ["Intermediate (lazy, returns a stream)", "Terminal (runs the pipeline)"], rows: [
        ["`filter`, `map`, `flatMap`", "`toList()`, `collect(...)`"],
        ["`sorted`, `distinct`", "`forEach`, `count`"],
        ["`limit`, `skip`", "`reduce`, `min`, `max`"],
        ["`peek` (debugging)", "`anyMatch`, `allMatch`, `noneMatch`"],
        ["`mapToInt`, `boxed`", "`findFirst`, `findAny`"]
      ] },

      { type: "h2", text: "Numbers, reduce and statistics" },
      { type: "code", language: "java", code: `int total = students.stream().mapToInt(Student::score).sum();
double avg = students.stream().mapToInt(Student::score).average().orElse(0);
IntSummaryStatistics stats = students.stream().mapToInt(Student::score).summaryStatistics();

System.out.println(total + " " + avg);
System.out.println(stats.getMin() + " " + stats.getMax());

int product = IntStream.rangeClosed(1, 5).reduce(1, (a, b) -> a * b);
System.out.println(product);`, output: `370 74.0
35 92
120` },

      { type: "h2", text: "Collectors: grouping and partitioning" },
      { type: "code", language: "java", code: `// Group by city → list of names
Map<String, List<String>> namesByCity = students.stream()
    .collect(Collectors.groupingBy(
        Student::city,
        TreeMap::new,                                   // sorted keys
        Collectors.mapping(Student::name, Collectors.toList())
    ));
System.out.println(namesByCity);

// Count per city
Map<String, Long> countByCity = students.stream()
    .collect(Collectors.groupingBy(Student::city, TreeMap::new, Collectors.counting()));
System.out.println(countByCity);

// Average score per city
Map<String, Double> avgByCity = students.stream()
    .collect(Collectors.groupingBy(Student::city, TreeMap::new,
                                   Collectors.averagingInt(Student::score)));
System.out.println(avgByCity);

// Pass / fail split
Map<Boolean, Long> passFail = students.stream()
    .collect(Collectors.partitioningBy(s -> s.score() >= 40, Collectors.counting()));
System.out.println(passFail);

// Join names
String csv = students.stream().map(Student::name).collect(Collectors.joining(", ", "[", "]"));
System.out.println(csv);`, output: `{Delhi=[Ravi, Kabir], Mumbai=[Zoya], Pune=[Asha, Meera]}
{Delhi=2, Mumbai=1, Pune=2}
{Delhi=54.5, Mumbai=81.0, Pune=90.0}
{false=1, true=4}
[Asha, Ravi, Meera, Kabir, Zoya]` },
      { type: "callout", variant: "note", text: "`Collectors.toMap(keyFn, valueFn)` throws `IllegalStateException` on duplicate keys. Pass a merge function, `toMap(k, v, (a, b) -> a)`, if duplicates are possible." },

      { type: "h2", text: "flatMap: flattening nested data" },
      { type: "code", language: "java", code: `record Course(String title, List<String> tags) {}

List<Course> courses = List.of(
    new Course("DSA", List.of("algorithms", "interviews")),
    new Course("System Design", List.of("scalability", "interviews"))
);

Set<String> allTags = courses.stream()
    .flatMap(c -> c.tags().stream())       // Stream<List<String>> → Stream<String>
    .collect(Collectors.toCollection(TreeSet::new));

System.out.println(allTags);`, output: `[algorithms, interviews, scalability]` },

      { type: "h2", text: "Optional: no more null checks" },
      { type: "code", language: "java", code: `Optional<Student> topDelhi = students.stream()
    .filter(s -> s.city().equals("Delhi"))
    .max(Comparator.comparingInt(Student::score));

String name = topDelhi.map(Student::name).orElse("nobody");
System.out.println(name);

students.stream()
    .filter(s -> s.city().equals("Chennai"))
    .findFirst()
    .ifPresentOrElse(
        s -> System.out.println("Found " + s.name()),
        () -> System.out.println("No students in Chennai")
    );`, output: `Kabir
No students in Chennai` },
      { type: "compare", badLabel: "Optional misuse", bad: `Optional<User> u = repo.findById(id);
if (u.isPresent()) {
    return u.get().getName();
}
return "Guest";`, goodLabel: "Fluent Optional", good: `return repo.findById(id)
    .map(User::getName)
    .orElse("Guest");` },
      { type: "ul", items: [
        "Use `Optional` as a **return type** for “might not exist”. Don’t use it for fields or parameters.",
        "Avoid `get()` without a check. Prefer `orElse`, `orElseGet`, `orElseThrow`, `map` and `ifPresent`.",
        "`orElse(compute())` always evaluates its argument. Use `orElseGet(() -> compute())` for expensive defaults."
      ] },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Side effects in streams", bad: `List<String> names = new ArrayList<>();
students.stream()
    .filter(s -> s.score() > 80)
    .forEach(s -> names.add(s.name()));`, goodLabel: "Collect results", good: `List<String> names = students.stream()
    .filter(s -> s.score() > 80)
    .map(Student::name)
    .toList();` },
      { type: "compare", badLabel: "Reusing a stream", bad: `Stream<Student> s = students.stream();
long count = s.count();
List<Student> list = s.toList();
// IllegalStateException: stream has already
// been operated upon or closed`, goodLabel: "New stream each time", good: `long count = students.size();
List<Student> list = students.stream()
    .filter(...)
    .toList();` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between intermediate and terminal operations?", answer: ["Intermediate operations (`filter`, `map`, `sorted`) return a new stream and are lazy: they only describe the pipeline. Terminal operations (`collect`, `toList`, `count`, `forEach`) trigger execution and produce a result or side effect, after which the stream is consumed."] },
      { type: "qa", question: "What is the difference between `map` and `flatMap`?", answer: ["`map` transforms each element into exactly one element. `flatMap` transforms each element into a stream and flattens all those streams into one, for example turning a list of courses with tag lists into a single stream of tags."] },
      { type: "qa", question: "What is a functional interface?", answer: ["An interface with exactly one abstract method, optionally annotated with `@FunctionalInterface`, so it can be implemented by a lambda or a method reference. Examples are `Runnable`, `Comparator`, `Predicate`, `Function`, `Supplier` and `Consumer`."] },
      { type: "qa", question: "Are streams always faster than loops?", answer: ["No. For simple operations, plain loops are often slightly faster. Streams win on readability and composability. Parallel streams can help for large CPU-bound workloads without shared state, but they add overhead and can hurt performance for small data or blocking I/O."] },
      { type: "qa", question: "How do you group a list into a map of counts?", answer: ["`list.stream().collect(Collectors.groupingBy(keyFunction, Collectors.counting()))`. Add a map factory such as `TreeMap::new` for sorted keys, or use downstream collectors such as `mapping`, `averagingInt` or `toSet` for other aggregations."] },
      { type: "qa", question: "What problem does Optional solve?", answer: ["It makes “might be absent” explicit in method return types and offers a fluent API (`map`, `filter`, `orElse`, `orElseThrow`) instead of scattered null checks, which reduces NullPointerExceptions."] },

      { type: "exercise", title: "Course report with streams", task: "Given `List<Enrollment>` where `record Enrollment(String student, String course, int progress)`, compute: (1) the number of students per course, sorted by course name; (2) the names of students who completed (progress == 100) any course, unique and sorted; (3) the course with the highest average progress.", language: "java", starter: `List<Enrollment> data = List.of(
    new Enrollment("Asha", "DSA", 100),
    new Enrollment("Ravi", "DSA", 40),
    new Enrollment("Asha", "Java", 75),
    new Enrollment("Meera", "Java", 100),
    new Enrollment("Kabir", "System Design", 90)
);`, solution: `// 1. students per course
Map<String, Long> perCourse = data.stream()
    .collect(Collectors.groupingBy(Enrollment::course, TreeMap::new, Collectors.counting()));

// 2. completers
List<String> completers = data.stream()
    .filter(e -> e.progress() == 100)
    .map(Enrollment::student)
    .distinct()
    .sorted()
    .toList();

// 3. best course by average progress
String best = data.stream()
    .collect(Collectors.groupingBy(Enrollment::course, Collectors.averagingInt(Enrollment::progress)))
    .entrySet().stream()
    .max(Map.Entry.comparingByValue())
    .map(Map.Entry::getKey)
    .orElse("none");

System.out.println(perCourse);
System.out.println(completers);
System.out.println(best);`, output: `{DSA=2, Java=2, System Design=1}
[Asha, Meera]
System Design`, explanation: "Grouping with `TreeMap::new` gives sorted course names. `distinct()` removes duplicate names before sorting. For the best course, group into average progress per course (DSA 70, Java 87.5, System Design 90), then stream the map entries and take the max by value." },

      { type: "summary", items: [
        "Lambdas implement functional interfaces, and method references are shorthand for simple lambdas.",
        "Streams are lazy pipelines (source → intermediate ops → one terminal op) and can’t be reused.",
        "Use `groupingBy`, `partitioningBy`, `mapping`, `counting` and `joining` for reports.",
        "Use `flatMap` to flatten nested collections.",
        "Return `Optional` for maybe-absent values, and use `map` and `orElse` instead of `get()`."
      ] }
    ]
  },

  {
    slug: "concurrency",
    title: "Multithreading and Concurrency",
    summary: "Threads, race conditions, synchronisation, ExecutorService, CompletableFuture and virtual threads.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Web servers handle many requests at once, batch jobs process data in parallel, and slow I/O shouldn’t block everything else. Concurrency makes this possible, but it also introduces some of the nastiest bugs in software. It is a core topic for Java backend interviews, especially at product companies." },

      { type: "h2", text: "The mental model: cooks in one kitchen" },
      { type: "p", text: "Threads are **cooks sharing one kitchen** (the process memory). More cooks get more done, but if two grab the same pan (shared data) at the same moment, the dish is ruined (a race condition). Locks are rules such as “only one cook at the stove”. Too many rules, or two cooks each waiting for the other’s pan (a deadlock), and the kitchen stops." },
      { type: "table", headers: ["Term", "Meaning"], rows: [
        ["Concurrency", "Dealing with many tasks at once by interleaving them (even on one core)"],
        ["Parallelism", "Doing many tasks at the same instant on several cores"],
        ["Thread", "An independent path of execution with its own stack, sharing the heap with other threads"],
        ["Race condition", "The result depends on the timing of threads accessing shared mutable state"]
      ] },

      { type: "h2", text: "Creating threads" },
      { type: "code", language: "java", code: `Runnable task = () -> System.out.println("Running in " + Thread.currentThread().getName());

Thread t = new Thread(task, "worker-1");
t.start();      // start(), not run(), creates a new thread
t.join();       // wait for it to finish` },
      { type: "callout", variant: "warning", text: "Calling `t.run()` just executes the code on the **current** thread, with no concurrency at all. Always call `start()`. In real applications, use an `ExecutorService` instead of creating threads by hand." },

      { type: "h2", text: "Race conditions" },
      { type: "code", language: "java", code: `class Counter {
    private int count = 0;
    void increment() { count++; }       // read → add → write: NOT atomic
    int get() { return count; }
}

Counter c = new Counter();
Runnable work = () -> { for (int i = 0; i < 100_000; i++) c.increment(); };

Thread a = new Thread(work), b = new Thread(work);
a.start(); b.start();
a.join(); b.join();
System.out.println(c.get());`, output: `137492   (varies each run, and rarely the expected 200000)` },
      { type: "p", text: "Both threads read the same value, both add 1, and both write it back, so one increment is lost. This is the classic **lost update**." },

      { type: "h2", text: "Fixing it: three tools" },
      { type: "code", language: "java", code: `// 1. synchronized: only one thread at a time inside the method (intrinsic lock)
class SyncCounter {
    private int count = 0;
    synchronized void increment() { count++; }
    synchronized int get() { return count; }
}

// 2. Atomic classes: lock-free, using compare-and-swap (CAS)
class AtomicCounter {
    private final AtomicInteger count = new AtomicInteger();
    void increment() { count.incrementAndGet(); }
    int get() { return count.get(); }
}

// 3. Explicit locks: more control (tryLock, timeouts, fairness)
class LockCounter {
    private final ReentrantLock lock = new ReentrantLock();
    private int count = 0;
    void increment() {
        lock.lock();
        try { count++; } finally { lock.unlock(); }   // always unlock in finally
    }
}` },
      { type: "table", headers: ["Tool", "Best for"], rows: [
        ["`AtomicInteger`, `AtomicLong`, `LongAdder`", "Simple counters and flags (`LongAdder` for heavy contention)"],
        ["`synchronized`", "Protecting a small critical section, the simplest choice"],
        ["`ReentrantLock`", "Timeouts, `tryLock`, interruptible locking, conditions"],
        ["`ConcurrentHashMap`, `CopyOnWriteArrayList`", "Thread-safe collections"],
        ["Immutable objects", "No locks needed at all: share freely"]
      ] },

      { type: "h2", text: "Visibility and volatile" },
      { type: "p", text: "Threads may cache variables, so one thread’s write might never be seen by another. `volatile` guarantees **visibility** (reads see the latest write) but **not atomicity**:" },
      { type: "code", language: "java", code: `class Worker implements Runnable {
    private volatile boolean running = true;    // without volatile, the loop may never stop

    public void run() {
        while (running) { /* do work */ }
    }

    public void stop() { running = false; }
}` },

      { type: "h2", text: "ExecutorService: thread pools" },
      { type: "p", text: "Creating a thread per task is expensive and unbounded. A **thread pool** reuses a fixed number of threads to run many tasks:" },
      { type: "code", language: "java", code: `ExecutorService pool = Executors.newFixedThreadPool(4);

List<Future<Integer>> results = new ArrayList<>();
for (String url : urls) {
    results.add(pool.submit(() -> fetchSize(url)));    // Callable returns a value
}

int total = 0;
for (Future<Integer> f : results) {
    total += f.get();               // blocks until that task finishes
}

pool.shutdown();                     // stop accepting tasks, finish queued ones
pool.awaitTermination(30, TimeUnit.SECONDS);` },

      { type: "h2", text: "CompletableFuture: composing async work" },
      { type: "code", language: "java", code: `CompletableFuture<User> userF = CompletableFuture.supplyAsync(() -> userService.find(id));
CompletableFuture<List<Course>> coursesF = CompletableFuture.supplyAsync(() -> courseService.forUser(id));

// Run both in parallel, then combine
CompletableFuture<Dashboard> dashboardF = userF.thenCombine(coursesF, Dashboard::new)
    .orTimeout(2, TimeUnit.SECONDS)
    .exceptionally(ex -> Dashboard.empty());   // fallback on failure

Dashboard dashboard = dashboardF.join();` },
      { type: "table", headers: ["Method", "Does"], rows: [
        ["`supplyAsync(supplier)`", "Run a task that returns a value asynchronously"],
        ["`thenApply(fn)`", "Transform the result (like `map`)"],
        ["`thenCompose(fn)`", "Chain another async call (like `flatMap`)"],
        ["`thenCombine(other, fn)`", "Combine two independent futures"],
        ["`allOf(...)`", "Wait for all of them"],
        ["`exceptionally` / `handle`", "Recover from errors"]
      ] },

      { type: "h2", text: "Virtual threads (Java 21)" },
      { type: "p", text: "Platform threads are heavy (about 1 MB of stack each, and managed by the OS), so servers could only run a few thousand. **Virtual threads** are lightweight threads managed by the JVM. You can run millions, and when one blocks on I/O, it releases its carrier OS thread:" },
      { type: "code", language: "java", code: `try (ExecutorService executor = Executors.newVirtualThreadPerTaskExecutor()) {
    for (int i = 0; i < 10_000; i++) {
        executor.submit(() -> {
            Thread.sleep(Duration.ofSeconds(1));   // blocking is cheap now
            return callExternalApi();
        });
    }
}   // waits for all tasks to finish

// Spring Boot 3.2+: spring.threads.virtual.enabled=true` },
      { type: "callout", variant: "tip", text: "Virtual threads make simple blocking code scale like async code for I/O-heavy servers. They don’t make CPU-bound work faster. For that, use a pool sized to the number of cores." },

      { type: "h2", text: "Deadlock" },
      { type: "code", language: "java", code: `// Thread 1: lock(A) → wants lock(B)
// Thread 2: lock(B) → wants lock(A)
// Both wait forever.

// Prevention: always acquire locks in the same global order,
// use tryLock with timeouts, and keep critical sections small.` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is a race condition and how do you prevent it?", answer: ["When the correctness of a computation depends on the interleaving of threads accessing shared mutable state, as with `count++` from two threads. Prevent it by making the operation atomic (`synchronized`, locks, atomic classes), using thread-safe collections, or avoiding shared mutable state with immutability or confinement."] },
      { type: "qa", question: "What is the difference between `synchronized` and `volatile`?", answer: ["`volatile` guarantees visibility and ordering of reads and writes to one variable, but not atomicity of compound actions such as `count++`. `synchronized` provides mutual exclusion and visibility for a whole block or method. Use `volatile` for simple flags and `synchronized` or atomics for read-modify-write operations."] },
      { type: "qa", question: "Why use a thread pool instead of creating threads?", answer: ["Creating threads is expensive, and unbounded creation can exhaust memory and CPU. Pools reuse threads, limit concurrency, queue extra tasks, and give you lifecycle control (shutdown and termination) and results through Futures."] },
      { type: "qa", question: "What is a deadlock and how can you avoid it?", answer: ["Two or more threads each hold a lock the other needs and wait forever. Avoid it by acquiring locks in a consistent global order, holding locks briefly, using `tryLock` with timeouts, and using higher-level concurrency utilities instead of manual locking."] },
      { type: "qa", question: "What are virtual threads?", answer: ["Lightweight threads (final in Java 21) scheduled by the JVM onto a small pool of OS carrier threads. Blocking I/O unmounts the virtual thread, so millions can exist cheaply, and simple thread-per-request blocking code scales for I/O-bound workloads without reactive programming."] },
      { type: "qa", question: "What is the difference between `Future` and `CompletableFuture`?", answer: ["`Future` represents a pending result. You can only block with `get()` or poll it. `CompletableFuture` adds non-blocking composition (`thenApply`, `thenCompose`, `thenCombine`, `allOf`), error recovery and timeouts, and can be completed manually."] },

      { type: "exercise", title: "Parallel price fetch with a timeout", task: "Write `Map<String, Integer> fetchPrices(List<String> courseIds)` that calls a slow `int priceOf(String id)` for every course in parallel using virtual threads, gives each call at most 2 seconds, and uses -1 for any course that fails or times out. Preserve the input order of the keys.", language: "java", solution: `public Map<String, Integer> fetchPrices(List<String> courseIds) {
    try (ExecutorService executor = Executors.newVirtualThreadPerTaskExecutor()) {
        Map<String, CompletableFuture<Integer>> futures = new LinkedHashMap<>();

        for (String id : courseIds) {
            futures.put(id, CompletableFuture
                .supplyAsync(() -> priceOf(id), executor)
                .orTimeout(2, TimeUnit.SECONDS)
                .exceptionally(ex -> -1));          // failure or timeout → -1
        }

        Map<String, Integer> prices = new LinkedHashMap<>();
        futures.forEach((id, f) -> prices.put(id, f.join()));
        return prices;
    }
}`, explanation: "Every call starts immediately on its own virtual thread, so the total time is about that of the slowest call (capped at 2s), not the sum. `orTimeout` combined with `exceptionally` turns slow or failing calls into a fallback value, and `LinkedHashMap` keeps the input order. The try-with-resources block shuts the executor down." },

      { type: "summary", items: [
        "Threads share memory, so shared mutable state needs coordination.",
        "Use atomics for counters, `synchronized` or locks for critical sections, and concurrent collections.",
        "`volatile` gives visibility, not atomicity.",
        "Use ExecutorService pools and CompletableFuture for composing async work.",
        "Java 21 virtual threads make blocking I/O code scale. Prevent deadlocks with consistent lock ordering."
      ] }
    ]
  },

  {
    slug: "spring-boot-rest",
    title: "REST APIs with Spring Boot",
    summary: "Build a production-style API: controllers, services, dependency injection, validation, DTOs and global error handling.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Spring Boot is the most widely used Java framework for backends in India and worldwide, at banks, e-commerce companies and SaaS firms. Java full-stack roles expect you to build clean REST APIs with it and explain what the framework does for you." },

      { type: "h2", text: "The mental model: a well-run office" },
      { type: "p", text: "Spring is an **office manager** (the IoC container). You describe the staff you need (beans) and who depends on whom, and the manager hires them, introduces them to each other (dependency injection) and manages their lifecycle. You focus on the actual work." },
      { type: "code", language: "text", code: `HTTP request
   │
   ▼
@RestController    → handles HTTP: paths, params, status codes
   │
   ▼
@Service           → business logic and transactions
   │
   ▼
@Repository        → database access (Spring Data JPA)
   │
   ▼
Database` },

      { type: "h2", text: "Dependency injection" },
      { type: "code", language: "java", code: `@Service
public class CourseService {
    private final CourseRepository repository;
    private final PricingService pricing;

    // Constructor injection: Spring passes in the beans automatically
    public CourseService(CourseRepository repository, PricingService pricing) {
        this.repository = repository;
        this.pricing = pricing;
    }
}` },
      { type: "table", headers: ["Injection style", "Verdict"], rows: [
        ["Constructor injection", "Recommended: dependencies are final and explicit, and easy to test with `new`"],
        ["Field injection (`@Autowired` on a field)", "Avoid: hidden dependencies, can’t be final, harder to test"],
        ["Setter injection", "For optional dependencies only"]
      ] },

      { type: "h2", text: "DTOs with records and validation" },
      { type: "code", language: "java", code: `public record CreateCourseRequest(
    @NotBlank @Size(max = 120) String title,
    @NotNull Level level,
    @PositiveOrZero int priceInRupees,
    @Size(max = 10) List<@NotBlank String> tags
) {}

public record CourseResponse(Long id, String title, Level level, int priceInRupees) {
    public static CourseResponse from(Course c) {
        return new CourseResponse(c.getId(), c.getTitle(), c.getLevel(), c.getPriceInRupees());
    }
}` },
      { type: "callout", variant: "tip", text: "Never expose JPA entities directly in your API. Separate request and response DTOs protect against mass assignment, avoid lazy-loading and serialisation problems, and let the database model change without breaking clients." },

      { type: "h2", text: "The controller" },
      { type: "code", language: "java", code: `@RestController
@RequestMapping("/api/courses")
public class CourseController {

    private final CourseService service;

    public CourseController(CourseService service) {
        this.service = service;
    }

    @GetMapping
    public Page<CourseResponse> list(
            @RequestParam(required = false) Level level,
            @PageableDefault(size = 20, sort = "createdAt", direction = Sort.Direction.DESC) Pageable pageable) {
        return service.list(level, pageable);
    }

    @GetMapping("/{id}")
    public CourseResponse get(@PathVariable Long id) {
        return service.get(id);
    }

    @PostMapping
    public ResponseEntity<CourseResponse> create(@Valid @RequestBody CreateCourseRequest request) {
        CourseResponse created = service.create(request);
        URI location = URI.create("/api/courses/" + created.id());
        return ResponseEntity.created(location).body(created);     // 201 + Location header
    }

    @DeleteMapping("/{id}")
    @ResponseStatus(HttpStatus.NO_CONTENT)
    public void delete(@PathVariable Long id) {
        service.delete(id);
    }
}` },
      { type: "table", headers: ["Annotation", "Reads"], rows: [
        ["`@PathVariable`", "`/api/courses/{id}` → `id`"],
        ["`@RequestParam`", "`?level=BEGINNER&page=0`"],
        ["`@RequestBody`", "The JSON body, deserialised by Jackson"],
        ["`@RequestHeader`", "A header such as `Authorization`"],
        ["`@Valid`", "Triggers Bean Validation on the DTO"]
      ] },

      { type: "h2", text: "The service" },
      { type: "code", language: "java", code: `@Service
public class CourseService {
    private final CourseRepository repository;

    public CourseService(CourseRepository repository) {
        this.repository = repository;
    }

    @Transactional(readOnly = true)
    public CourseResponse get(Long id) {
        return repository.findById(id)
                .map(CourseResponse::from)
                .orElseThrow(() -> new CourseNotFoundException(id));
    }

    @Transactional
    public CourseResponse create(CreateCourseRequest req) {
        if (repository.existsByTitleIgnoreCase(req.title())) {
            throw new DuplicateCourseException(req.title());
        }
        Course course = new Course(req.title(), req.level(), req.priceInRupees());
        return CourseResponse.from(repository.save(course));
    }
}` },

      { type: "h2", text: "Global error handling" },
      { type: "code", language: "java", code: `@RestControllerAdvice
public class GlobalExceptionHandler {

    @ExceptionHandler(CourseNotFoundException.class)
    public ProblemDetail notFound(CourseNotFoundException ex) {
        return ProblemDetail.forStatusAndDetail(HttpStatus.NOT_FOUND, ex.getMessage());
    }

    @ExceptionHandler(DuplicateCourseException.class)
    public ProblemDetail conflict(DuplicateCourseException ex) {
        return ProblemDetail.forStatusAndDetail(HttpStatus.CONFLICT, ex.getMessage());
    }

    @ExceptionHandler(MethodArgumentNotValidException.class)
    public ProblemDetail validation(MethodArgumentNotValidException ex) {
        ProblemDetail pd = ProblemDetail.forStatusAndDetail(HttpStatus.BAD_REQUEST, "Validation failed");
        Map<String, String> errors = new LinkedHashMap<>();
        ex.getBindingResult().getFieldErrors()
          .forEach(e -> errors.put(e.getField(), e.getDefaultMessage()));
        pd.setProperty("errors", errors);
        return pd;
    }

    @ExceptionHandler(Exception.class)
    public ProblemDetail unexpected(Exception ex) {
        log.error("Unhandled error", ex);                          // details in the logs only
        return ProblemDetail.forStatus(HttpStatus.INTERNAL_SERVER_ERROR);
    }
}` },
      { type: "code", language: "json", code: `// POST /api/courses with { "title": "", "priceInRupees": -10 }
{
  "type": "about:blank",
  "title": "Bad Request",
  "status": 400,
  "detail": "Validation failed",
  "errors": {
    "title": "must not be blank",
    "level": "must not be null",
    "priceInRupees": "must be greater than or equal to 0"
  }
}` },

      { type: "h2", text: "Configuration and profiles" },
      { type: "code", language: "yaml", code: `# application.yml
spring:
  application:
    name: codeazy-api
  datasource:
    url: \${DB_URL}
    username: \${DB_USER}
    password: \${DB_PASSWORD}
  threads:
    virtual:
      enabled: true
server:
  port: 8080

---
spring:
  config:
    activate:
      on-profile: dev
  jpa:
    show-sql: true` },
      { type: "p", text: "Use profiles (`dev`, `test`, `prod`) for environment-specific settings, and read secrets from environment variables. Bind related settings into type-safe classes with `@ConfigurationProperties`." },

      { type: "h2", text: "What Spring Boot auto-configures" },
      { type: "ul", items: [
        "An embedded Tomcat server, so you run a JAR rather than deploying a WAR.",
        "Jackson JSON serialisation for `@RestController` return values.",
        "A DataSource, connection pool (HikariCP) and JPA setup from your `spring.datasource.*` properties.",
        "Actuator endpoints (`/actuator/health`, `/actuator/metrics`) when the starter is added.",
        "These are sensible defaults, and you can override any of them by defining your own bean or property."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is dependency injection and why does Spring use it?", answer: ["Objects receive their dependencies from the outside instead of creating them. Spring’s IoC container creates beans and injects them, usually through constructors. This decouples classes from concrete implementations, makes testing easy with mocks or fakes, and centralises configuration and lifecycle."] },
      { type: "qa", question: "What is the difference between `@Component`, `@Service`, `@Repository` and `@Controller`?", answer: ["All are stereotypes that register a bean through component scanning. `@Service` marks business logic. `@Repository` marks data access and enables translation of persistence exceptions. `@Controller` and `@RestController` handle web requests, and `@RestController` adds `@ResponseBody` so return values become JSON."] },
      { type: "qa", question: "How do you handle exceptions globally in Spring Boot?", answer: ["With a `@RestControllerAdvice` class containing `@ExceptionHandler` methods that map exception types to HTTP responses, ideally using RFC 7807 `ProblemDetail`. This keeps controllers clean and error responses consistent."] },
      { type: "qa", question: "Why use DTOs instead of returning entities?", answer: ["DTOs decouple the API contract from the database model, prevent over-posting (mass assignment), avoid exposing sensitive fields, prevent lazy-loading and serialisation issues with JPA proxies, and let you shape responses for each endpoint."] },
      { type: "qa", question: "What does `@SpringBootApplication` do?", answer: ["It combines `@Configuration` (the class can define beans), `@EnableAutoConfiguration` (configure beans based on the classpath and properties) and `@ComponentScan` (find components in this package and its sub-packages)."] },
      { type: "qa", question: "What is the default scope of a Spring bean?", answer: ["Singleton: one shared instance per application context. That is why beans should be stateless, or hold only thread-safe state, because many request threads use the same instance at the same time. Other scopes include prototype, request and session."] },

      { type: "exercise", title: "Add an enrolment endpoint", task: "Add `POST /api/courses/{id}/enrollments` that accepts `{ \"studentEmail\": \"...\" }` (validated as a non-blank email), returns `201` with an `EnrollmentResponse(Long id, String courseTitle, String studentEmail)`, returns `404` if the course doesn’t exist and `409` if the student is already enrolled. Show the request DTO, controller method and service method.", language: "java", solution: `public record EnrollRequest(@NotBlank @Email String studentEmail) {}

public record EnrollmentResponse(Long id, String courseTitle, String studentEmail) {}

// Controller
@PostMapping("/{id}/enrollments")
public ResponseEntity<EnrollmentResponse> enroll(@PathVariable Long id,
                                                 @Valid @RequestBody EnrollRequest request) {
    EnrollmentResponse res = enrollmentService.enroll(id, request.studentEmail());
    return ResponseEntity
            .created(URI.create("/api/courses/" + id + "/enrollments/" + res.id()))
            .body(res);
}

// Service
@Transactional
public EnrollmentResponse enroll(Long courseId, String email) {
    Course course = courseRepository.findById(courseId)
            .orElseThrow(() -> new CourseNotFoundException(courseId));     // → 404

    if (enrollmentRepository.existsByCourseIdAndStudentEmail(courseId, email)) {
        throw new AlreadyEnrolledException(email, course.getTitle());     // → 409
    }

    Enrollment saved = enrollmentRepository.save(new Enrollment(course, email));
    return new EnrollmentResponse(saved.getId(), course.getTitle(), email);
}

// Advice
@ExceptionHandler(AlreadyEnrolledException.class)
public ProblemDetail alreadyEnrolled(AlreadyEnrolledException ex) {
    return ProblemDetail.forStatusAndDetail(HttpStatus.CONFLICT, ex.getMessage());
}`, explanation: "The controller only deals with HTTP: validation, status codes and the Location header. The service owns the business rules and the transaction, and the advice maps domain exceptions to status codes. Back the duplicate check with a unique database constraint on `(course_id, student_email)` so concurrent requests can’t slip through." },

      { type: "summary", items: [
        "Spring’s IoC container creates and wires beans. Use constructor injection.",
        "Layer the app: controller (HTTP), service (business logic and transactions), repository (data).",
        "Use record DTOs with Bean Validation (`@Valid`), and never expose entities.",
        "Centralise errors in `@RestControllerAdvice` returning `ProblemDetail`.",
        "Configure with profiles and environment variables, and rely on auto-configuration you can override."
      ] }
    ]
  },

  {
    slug: "spring-data-jpa",
    title: "Databases with Spring Data JPA",
    summary: "Entities, relationships, repositories, queries, transactions and the N+1 problem every Java developer must know.",
    readTime: 15,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Most Java backends store data in relational databases such as PostgreSQL or MySQL through JPA and Hibernate. Spring Data JPA removes most boilerplate, but misunderstanding lazy loading, transactions or the N+1 problem leads to slow and buggy applications. These are core interview topics for Java roles." },

      { type: "h2", text: "The mental model: an interpreter between two languages" },
      { type: "p", text: "Your code speaks **objects** (a Course with a list of Lessons), and the database speaks **tables and rows** (`courses`, `lessons` with a `course_id` foreign key). JPA and Hibernate form an **ORM** (object-relational mapper), an interpreter that translates between them, so `course.getLessons()` becomes the right SQL." },
      { type: "table", headers: ["Term", "Meaning"], rows: [
        ["JPA", "The Java specification (the interfaces and annotations)"],
        ["Hibernate", "The most popular JPA implementation, and Spring Boot’s default"],
        ["Spring Data JPA", "Generates repository implementations on top of JPA"],
        ["Entity", "A class mapped to a table"],
        ["Persistence context", "The first-level cache of managed entities within a transaction"]
      ] },

      { type: "h2", text: "Entities" },
      { type: "code", language: "java", code: `@Entity
@Table(name = "courses")
public class Course {

    @Id
    @GeneratedValue(strategy = GenerationType.IDENTITY)
    private Long id;

    @Column(nullable = false, length = 120, unique = true)
    private String title;

    @Enumerated(EnumType.STRING)          // store "BEGINNER", not 0
    private Level level;

    private int priceInRupees;

    @OneToMany(mappedBy = "course", cascade = CascadeType.ALL, orphanRemoval = true)
    @OrderBy("position ASC")
    private List<Lesson> lessons = new ArrayList<>();

    @CreationTimestamp
    private Instant createdAt;

    protected Course() {}                 // required by JPA

    public Course(String title, Level level, int priceInRupees) {
        this.title = title;
        this.level = level;
        this.priceInRupees = priceInRupees;
    }

    public void addLesson(Lesson lesson) { // keep both sides in sync
        lessons.add(lesson);
        lesson.setCourse(this);
    }
}

@Entity
@Table(name = "lessons")
public class Lesson {
    @Id @GeneratedValue(strategy = GenerationType.IDENTITY)
    private Long id;

    private String title;
    private int position;

    @ManyToOne(fetch = FetchType.LAZY, optional = false)
    @JoinColumn(name = "course_id")
    private Course course;
    // getters and setters...
}` },
      { type: "table", headers: ["Relationship", "Annotation", "Default fetch"], rows: [
        ["Many lessons → one course", "`@ManyToOne`", "EAGER (set it to LAZY!)"],
        ["One course → many lessons", "`@OneToMany(mappedBy = ...)`", "LAZY"],
        ["Student ↔ course", "`@ManyToMany`, or better, an `Enrollment` entity", "LAZY"],
        ["User → profile", "`@OneToOne`", "EAGER (set it to LAZY)"]
      ] },
      { type: "callout", variant: "warning", text: "Always use `@Enumerated(EnumType.STRING)`. The default `ORDINAL` stores 0, 1, 2, so inserting a new enum value in the middle silently corrupts existing data." },

      { type: "h2", text: "Repositories" },
      { type: "code", language: "java", code: `public interface CourseRepository extends JpaRepository<Course, Long> {

    // Derived queries: Spring generates the SQL from the method name
    Optional<Course> findByTitleIgnoreCase(String title);
    boolean existsByTitleIgnoreCase(String title);
    List<Course> findByLevelOrderByPriceInRupeesAsc(Level level);
    Page<Course> findByPriceInRupeesLessThanEqual(int max, Pageable pageable);

    // JPQL for anything more complex
    @Query("select c from Course c where lower(c.title) like lower(concat('%', :q, '%'))")
    List<Course> search(@Param("q") String q);

    // Projection: fetch only what you need
    @Query("select new com.codeazy.dto.CourseSummary(c.id, c.title, size(c.lessons)) from Course c")
    List<CourseSummary> summaries();
}` },
      { type: "p", text: "`JpaRepository` already provides `save`, `findById`, `findAll(Pageable)`, `deleteById`, `count` and more, with no implementation code." },

      { type: "h2", text: "The N+1 problem" },
      { type: "code", language: "java", code: `List<Course> courses = courseRepository.findAll();          // 1 query
for (Course c : courses) {
    System.out.println(c.getLessons().size());              // +1 query PER course
}
// 100 courses → 101 queries` },
      { type: "p", text: "Lazy collections are loaded on first access, one query per parent. It looks harmless in code but kills performance in production. The fixes:" },
      { type: "code", language: "java", code: `// 1. Fetch join: load courses and lessons in one query
@Query("select distinct c from Course c left join fetch c.lessons")
List<Course> findAllWithLessons();

// 2. Entity graph
@EntityGraph(attributePaths = "lessons")
List<Course> findByLevel(Level level);

// 3. Batch fetching (application.yml): load lazy collections in batches
// spring.jpa.properties.hibernate.default_batch_fetch_size: 50

// 4. DTO projection: often the best for read endpoints
@Query("select new com.codeazy.dto.CourseSummary(c.id, c.title, count(l)) " +
       "from Course c left join c.lessons l group by c.id, c.title")
List<CourseSummary> summariesWithLessonCount();` },
      { type: "callout", variant: "tip", text: "Turn on SQL logging in development (`spring.jpa.show-sql=true`, or better, a datasource proxy that counts queries) and look at how many queries each endpoint runs. N+1 problems show up immediately." },

      { type: "h2", text: "Transactions" },
      { type: "code", language: "java", code: `@Service
public class EnrollmentService {

    @Transactional                             // all or nothing
    public void enroll(Long cohortId, Long studentId) {
        Cohort cohort = cohortRepository.findById(cohortId).orElseThrow();
        if (cohort.getSeatsLeft() == 0) throw new BatchFullException(cohortId);

        cohort.setSeatsLeft(cohort.getSeatsLeft() - 1);   // no save() needed: dirty checking
        enrollmentRepository.save(new Enrollment(cohort, studentId));
    }   // commit here, or roll back if a RuntimeException was thrown
}` },
      { type: "ul", items: [
        "**Dirty checking:** changes to managed entities are flushed automatically at commit, so you don’t need to call `save()` for updates.",
        "**Rollback rules:** by default, rollback happens for unchecked exceptions, not for checked ones (configure `rollbackFor`).",
        "**Self-invocation:** calling a `@Transactional` method from another method in the same class bypasses the proxy, so **no transaction** is started.",
        "**`readOnly = true`** on query methods lets Hibernate skip dirty checking, which is a small performance win.",
        "**Concurrency:** for the seat-count example, use optimistic locking (`@Version`) or a conditional update to prevent overbooking under load."
      ] },
      { type: "code", language: "java", code: `@Entity
public class Cohort {
    @Version
    private long version;      // optimistic lock: a concurrent update fails with an exception
    private int seatsLeft;
}` },

      { type: "h2", text: "Schema migrations" },
      { type: "p", text: "Never use `spring.jpa.hibernate.ddl-auto=update` in production. Version your schema changes with **Flyway** or **Liquibase**:" },
      { type: "code", language: "sql", code: `-- src/main/resources/db/migration/V3__add_enrollments.sql
CREATE TABLE enrollments (
    id            BIGSERIAL PRIMARY KEY,
    cohort_id     BIGINT NOT NULL REFERENCES cohorts(id),
    student_email VARCHAR(255) NOT NULL,
    created_at    TIMESTAMPTZ NOT NULL DEFAULT now(),
    CONSTRAINT uq_enrollment UNIQUE (cohort_id, student_email)
);
CREATE INDEX idx_enrollments_student ON enrollments(student_email);` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the N+1 query problem and how do you fix it?", answer: ["Loading N parent entities with one query, then triggering one extra query per parent when accessing a lazy association, for N+1 queries in total. Fix it with fetch joins, `@EntityGraph`, Hibernate batch fetching, or DTO projections that select exactly the needed columns."] },
      { type: "qa", question: "What is the difference between LAZY and EAGER fetching?", answer: ["LAZY loads an association only when it is accessed, through a proxy. EAGER loads it immediately with the parent. Prefer LAZY everywhere (`@ManyToOne` and `@OneToOne` default to EAGER, so override them) and fetch what each use case needs explicitly, to avoid loading huge object graphs."] },
      { type: "qa", question: "Why might `@Transactional` not work?", answer: ["Common causes: calling the method from the same class (self-invocation bypasses the Spring proxy), the method isn’t public, the exception is checked (no rollback by default), or the exception is caught and swallowed inside the method."] },
      { type: "qa", question: "What is the persistence context?", answer: ["The set of managed entity instances within a transaction or EntityManager, which acts as a first-level cache. Each database row maps to one object instance, changes are tracked (dirty checking) and flushed automatically, and repeated `findById` calls for the same ID return the cached instance."] },
      { type: "qa", question: "What is the difference between optimistic and pessimistic locking?", answer: ["Optimistic locking assumes conflicts are rare. A `@Version` column is checked on update, and a conflicting update fails with an exception that you retry or report. Pessimistic locking takes a database lock (`SELECT ... FOR UPDATE`) so others wait. Optimistic locking scales better, and pessimistic locking suits highly contended rows."] },
      { type: "qa", question: "Why use Flyway or Liquibase instead of `ddl-auto=update`?", answer: ["Migrations are versioned, reviewable, repeatable and reversible SQL scripts that run the same in every environment. `ddl-auto=update` can’t handle renames or data migrations safely and may make unexpected changes to production schemas."] },

      { type: "exercise", title: "Fix the slow dashboard", task: "This endpoint takes 4 seconds with 300 courses: `courseRepository.findAll().stream().map(c -> new CourseCard(c.getTitle(), c.getLessons().size(), c.getInstructor().getName())).toList()`. Lessons are `@OneToMany` (lazy) and instructor is `@ManyToOne` (default fetch). Explain the queries it runs and rewrite it efficiently.", language: "java", solution: `// Problem: 1 query for courses
//        + 300 queries for lessons (lazy collection per course)
//        + instructor loaded EAGERLY with every course (or per course)
//        → hundreds of queries, and full Lesson entities just to count them

// Fix: one DTO projection query that returns exactly what the card needs
public record CourseCard(String title, long lessonCount, String instructorName) {}

public interface CourseRepository extends JpaRepository<Course, Long> {

    @Query("""
        select new com.codeazy.dto.CourseCard(c.title, count(l), i.name)
        from Course c
        join c.instructor i
        left join c.lessons l
        group by c.id, c.title, i.name
        order by c.title
        """)
    List<CourseCard> findCourseCards();
}

// Also make @ManyToOne lazy on the entity to avoid eager loading elsewhere:
// @ManyToOne(fetch = FetchType.LAZY) private Instructor instructor;`, explanation: "The original code triggers the N+1 problem for lessons and eagerly loads instructors. The projection asks the database to join and count in a single SQL statement, returning 300 small DTOs instead of thousands of entities. The response time drops from seconds to milliseconds." },

      { type: "summary", items: [
        "JPA and Hibernate map objects to tables. Spring Data generates repositories.",
        "Use LAZY associations, `EnumType.STRING`, and keep both sides of a relationship in sync.",
        "Detect and fix N+1 with fetch joins, entity graphs, batch fetching or DTO projections.",
        "`@Transactional` gives all-or-nothing work with dirty checking. Beware self-invocation and checked exceptions.",
        "Use `@Version` for optimistic locking, and Flyway or Liquibase for schema changes."
      ] }
    ]
  }
];
